import { isResolvedAck, isRetiredFinding, withSavedFindingEvidence } from './findingAck';

// Scoring helpers — V2 penalty-based (jedyny scoring od 2026-05-05)
// Spec: PROJEKTY/AUDYTOR/SPEC-scoring-v2.md
// Engine: src/engine/audit-rules.ts (scoreV2 inline w runAudit + runAuditAsync)

// V2 thresholds: ≥8.5 success, ≥6 warning, <6 error
export function getScoreThresholds() {
    return { good: 8.5, warn: 6 };
}

export function scoreTextColor(score) {
    if (score >= 8.5) return 'text-emerald-600 dark:text-emerald-400';
    if (score >= 6) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
}

export function scoreBgColor(score) {
    if (score >= 8.5) return 'bg-emerald-500';
    if (score >= 6) return 'bg-amber-500';
    return 'bg-red-500';
}

export function scoreHexColor(score) {
    if (score >= 8.5) return '#10b981';
    if (score >= 6) return '#f59e0b';
    return '#ef4444';
}

// Penalty breakdown — read directly from report (post-cleanup: scoreBreakdown lub legacy scoreV2.perRule)
export function getPenaltyBreakdown(report) {
    if (!report) return [];
    if (Array.isArray(report.scoreBreakdown)) return report.scoreBreakdown;
    if (report.scoreV2 && Array.isArray(report.scoreV2.perRule)) return report.scoreV2.perRule;
    return [];
}

export function getTotalPenalty(report) {
    if (!report) return 0;
    if (typeof report.totalPenalty === 'number') return report.totalPenalty;
    if (report.scoreV2 && typeof report.scoreV2.totalPenalty === 'number') return report.scoreV2.totalPenalty;
    return 0;
}

// pickScore: zwraca obj.score (zawsze V2 od cleanup). Legacy obj.scoreV2.score zachowany dla starych runów w DB.
export function pickScore(obj) {
    if (!obj) return 0;
    if (typeof obj.score === 'number') return obj.score;
    if (obj.scoreV2 && typeof obj.scoreV2.score === 'number') return obj.scoreV2.score;
    return 0;
}

export { isRetiredFinding };

/** Presentation copy only: persisted evidence and historical score are untouched. */
export function reportForPresentation(report) {
 if (!Array.isArray(report?.issues)) return report;
 const restoredIssues = withSavedFindingEvidence(report.issues);
 if (restoredIssues !== report.issues) report = { ...report, issues: restoredIssues };
 const hidden = report.issues.filter(isRetiredFinding);
 if (!hidden.length) return report;
 const issues = report.issues.filter(issue=>!isRetiredFinding(issue));
 const categorySummary = Object.fromEntries(Object.entries(report.categorySummary||{}).map(([category,summary])=>{
  const removed = hidden.filter(issue=>issue.category===category && !['financial_result','scoring','health','rule_not_checked','review_signal'].includes(issue.specialType));
  return [category,{...summary,
   errors:Math.max(0,(summary.errors||0)-removed.filter(i=>i.type==='error').length),
   warnings:Math.max(0,(summary.warnings||0)-removed.filter(i=>i.type==='warning').length),
   total:Math.max(0,(summary.total||0)-removed.length),
   reviewSignals:Math.max(0,(summary.reviewSignals||0)-hidden.filter(i=>i.category===category && i.specialType==='review_signal').length),
  }];
 }));
 return {...report,issues,categorySummary,
  errCount:issues.filter(i=>i.type==='error' && !['financial_result','scoring','health','rule_not_checked','review_signal'].includes(i.specialType)).length,
  warnCount:issues.filter(i=>i.type==='warning' && !['financial_result','scoring','health','rule_not_checked','review_signal'].includes(i.specialType)).length,
  presentationRetiredRules:[...new Set([...(report.presentationRetiredRules||[]),...hidden.map(i=>i.ruleId)])],
 };
}

// Persisted reports and backend summaries use the same scope contract.
export function auditScope(report, getAck) {
    report = reportForPresentation(report);
    const signals = Array.isArray(report?.issues)
        ? report.issues.filter(i => i.specialType === 'review_signal' && !isResolvedAck(getAck?.(i.ruleId, i))).length
        : Number(report?.reviewSignalCount || report?.review_signal_count || 0);
    const unchecked = Math.max(report?.notCheckedRules?.length || 0,
        report?.issues?.find(i => i.specialType === 'coverage')?.details?.length || 0,
        Number(report?.uncheckedCount || 0), Number(report?.unchecked_count || 0));
    const coverageLimited = report?.coverage_complete === false || report?.coverageComplete === false || report?.status === 'incomplete';
    return { signals, unchecked, historical: !!report?.presentationRetiredRules?.length,
        limited: coverageLimited || signals > 0 || unchecked > 0 || !!report?.presentationRetiredRules?.length };
}

// Shared by the screen, printable template and the actual PDF export header.
export function auditPresentation(report, getAck) {
    const score = pickScore(report);
    const scope = auditScope(report, getAck);
    const neutral = scope.historical || (score >= getScoreThresholds().good && scope.limited);
    const verdictKey = scope.historical ? 'audit_history_score' : neutral ? 'audit_score_limited' : score >= 9.5 ? 'verdict_excellent'
        : score >= 8.5 ? 'verdict_good' : score >= 6 ? 'verdict_acceptable'
        : score >= 3 ? 'verdict_needs_fix' : 'verdict_critical';
    return { ...scope, score, neutral, verdictKey,
        gaugeColor: neutral ? '#64748b' : scoreHexColor(score),
        textColor: neutral ? 'text-slate-600 dark:text-slate-400' : scoreTextColor(score) };
}
